import { isAbort, retry } from "../util/async";

/**
 * Trimble Connect Core (project) API regions.
 *
 * A project lives in exactly one region and its data is only reachable through that
 * region's host, so the correct base URL is discovered once by asking each candidate for
 * the project and keeping the first that answers.
 */
export const CORE_API_HOSTS = [
  { name: "US", url: "https://app.connect.trimble.com/tc/api/2.0" },
  { name: "EU", url: "https://app21.connect.trimble.com/tc/api/2.0" },
  { name: "UK", url: "https://app22.connect.trimble.com/tc/api/2.0" },
  { name: "AP", url: "https://app31.connect.trimble.com/tc/api/2.0" },
  { name: "AP2", url: "https://app32.connect.trimble.com/tc/api/2.0" },
] as const;

export const CORE_API_HOSTS_STAGE = [
  { name: "US (stage)", url: "https://app.stage.connect.trimble.com/tc/api/2.0" },
  { name: "EU (stage)", url: "https://app21.stage.connect.trimble.com/tc/api/2.0" },
  { name: "AP (stage)", url: "https://app31.stage.connect.trimble.com/tc/api/2.0" },
] as const;

export interface TcProject {
  id: string;
  name?: string;
  rootId?: string;
  location?: string;
  crs?: { name?: string; csib64?: string };
}

export interface TcItem {
  id: string;
  name: string;
  /** `FOLDER` or `FILE`. */
  type: string;
  versionId?: string;
  parentId?: string;
  size?: number;
  hasChildren?: boolean;
  modifiedOn?: string;
}

export interface TcDownload {
  id: string;
  versionId: string;
  url: string;
}

/**
 * Response of both the initiate (`POST /files/fs/upload`, status 202) and status
 * (`GET /files/fs/upload?uploadId=…`) calls of the "Package File upload" flow.
 */
interface FileUploadDetailsResponse {
  uploadId: string;
  /** `UPLOADABLE` right after initiate, then `DONE` / `ERROR` / `EXPIRED`. */
  status?: string;
  /** Set once `status` is `DONE`. */
  fileId?: string;
  versionId?: string;
  errorReason?: string;
  /** One entry per uploaded part; a plain single-part upload has exactly one, the source file. */
  contents?: { url?: string }[];
}

/** One object covered by a share link. */
export interface TcShareObject {
  id: string;
  /** `FILE` or `FOLDER`. */
  type: string;
  name?: string;
  versionId?: string;
}

export interface TcShare {
  id: string;
  projectId: string;
  /** `PUBLIC` for an open link, or a restricted mode. */
  mode?: string;
  /** `VIEW`, `DOWNLOAD`, … */
  permission?: string;
  objects: TcShareObject[];
}

export class CoreApi {
  private baseUrl: string | null = null;
  /** Set when a share link is opened; takes precedence over the session token. */
  private tokenOverride: string | null = null;

  constructor(
    private readonly getToken: () => Promise<string>,
    options: { baseUrl?: string } = {},
  ) {
    this.baseUrl = options.baseUrl ?? null;
  }

  private token(): Promise<string> {
    return this.tokenOverride ? Promise.resolve(this.tokenOverride) : this.getToken();
  }

  get resolvedBaseUrl(): string | null {
    return this.baseUrl;
  }

  setBaseUrl(url: string | null): void {
    this.baseUrl = url ? url.replace(/\/+$/, "") : null;
  }

  /**
   * Finds the region hosting `projectId` and pins the base URL to it.
   *
   * Candidate hosts are probed in parallel, but this resolves as soon as the first one
   * answers successfully instead of waiting for all of them to settle — with up to 8
   * regions (5 production + 3 staging) to check, waiting for the slowest straggler to
   * time out added seconds to every cold start even though the right region usually
   * answers almost immediately. The rest are aborted once a winner is found. The
   * project's `location` field, when known, is only a hint (used to try the likely
   * region first) because its spelling is not part of the API contract.
   */
  async resolveRegion(projectId: string, hint?: string): Promise<TcProject> {
    if (this.baseUrl) return this.request<TcProject>(`/projects/${encodeURIComponent(projectId)}`);

    const token = await this.token();
    const candidates = orderCandidates(hint);
    const controller = new AbortController();

    return new Promise<TcProject>((resolve, reject) => {
      const errors: string[] = [];
      let pending = candidates.length;

      for (const candidate of candidates) {
        fetch(`${candidate.url}/projects/${encodeURIComponent(projectId)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
          .then(async (response) => {
            if (!response.ok) throw new Error(`${candidate.name}: HTTP ${response.status}`);
            return (await response.json()) as TcProject;
          })
          .then((project) => {
            this.baseUrl = candidate.url;
            controller.abort();
            resolve(project);
          })
          .catch((error) => {
            if (isAbort(error)) return; // lost the race, not a real failure
            errors.push(`${candidate.name}: ${error instanceof Error ? error.message : String(error)}`);
            if (--pending === 0) {
              reject(new Error(`Could not reach the project in any Trimble Connect region (${errors.join("; ")})`));
            }
          });
      }
    });
  }

  async getProject(projectId: string): Promise<TcProject> {
    return this.request<TcProject>(`/projects/${encodeURIComponent(projectId)}`);
  }

  async listFolder(folderId: string): Promise<TcItem[]> {
    return this.request<TcItem[]>(`/folders/${encodeURIComponent(folderId)}/items`);
  }

  async getFile(fileId: string): Promise<TcItem> {
    return this.request<TcItem>(`/files/${encodeURIComponent(fileId)}`);
  }

  /**
   * Returns a short-lived, pre-signed download URL for a file version.
   *
   * These URLs support HTTP range requests, which is what lets the viewer stream tiles out
   * of a multi-gigabyte GeoTIFF or mesh archive instead of downloading it.
   */
  async getDownloadUrl(fileId: string, versionId?: string): Promise<string> {
    const query = versionId ? `?versionId=${encodeURIComponent(versionId)}` : "";
    const result = await this.request<TcDownload>(
      `/files/fs/${encodeURIComponent(fileId)}/downloadurl${query}`,
    );
    if (!result?.url) throw new Error(`Trimble Connect returned no download URL for file ${fileId}`);
    return result.url;
  }

  /**
   * Exchanges a public share token for the access token that covers the shared objects,
   * pinning the API to whichever region answered.
   *
   * The exchange itself is unauthenticated, which is the whole point: it is what lets a
   * share link be opened without a Trimble Identity session or a registered application.
   */
  async openShare(shareToken: string, hintBaseUrl?: string): Promise<TcShare> {
    const candidates = hintBaseUrl
      ? [{ name: "link", url: hintBaseUrl.replace(/\/+$/, "") }, ...CORE_API_HOSTS]
      : [...CORE_API_HOSTS, ...CORE_API_HOSTS_STAGE];

    const errors: string[] = [];
    for (const candidate of candidates) {
      try {
        const response = await fetch(
          `${candidate.url}/shares/token/${encodeURIComponent(shareToken)}`,
          { headers: { Accept: "application/json" } },
        );
        if (!response.ok) {
          errors.push(`${candidate.name}: HTTP ${response.status}`);
          continue;
        }
        const share = (await response.json()) as TcShare & { accessToken?: string };
        if (!share.accessToken) {
          errors.push(`${candidate.name}: share returned no access token`);
          continue;
        }
        this.baseUrl = candidate.url;
        this.tokenOverride = share.accessToken;
        return share;
      } catch (error) {
        errors.push(`${candidate.name}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    throw new Error(`Could not open the share link (${errors.join("; ")})`);
  }

  /** True when the client is authenticated by a share link rather than a user session. */
  get usingShareToken(): boolean {
    return this.tokenOverride !== null;
  }

  /**
   * The share's own scoped access token, when this client was authenticated by
   * {@link openShare} — distinct from the signed-in user's own session token
   * (`WorkspaceBridge.accessToken`), which is what the server's identity gate verifies. Used
   * to let the server resolve a share-opened mesh archive's download URL with the same
   * scoped credential the browser would use, without conflating it with the caller's own
   * identity (see `server/src/mesh/archive.ts`).
   */
  get shareAccessToken(): string | null {
    return this.tokenOverride;
  }

  clearShareToken(): void {
    this.tokenOverride = null;
  }

  /** Downloads a file's current content as text, e.g. a small JSON sidecar. */
  async downloadText(fileId: string, versionId?: string): Promise<string> {
    const url = await this.getDownloadUrl(fileId, versionId);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download of ${fileId} failed: HTTP ${response.status}`);
    return response.text();
  }

  /**
   * Creates a file in `parentId`, or — when a file of the same name already exists there —
   * versions it, which is how Trimble Connect's own upload UI treats a same-name upload.
   *
   * Follows the "Package File upload" flow (`POST /files/fs/upload`, still current — not to
   * be confused with the deprecated flat `POST /files/fs/initiate` + `POST /files/fs/commit`
   * pair, which uses a different, top-level `{uploadId, uploadURL}` response shape). Here the
   * initiate call answers 202 with a `FileUploadDetailsResponse`: the actual PUT target is
   * `contents[0].url`, not a top-level field. There is no separate commit step for this flow
   * — the upload is finalised server-side once the PUT completes, and progress is read back
   * by polling `GET /files/fs/upload?uploadId=…&wait=true` (which long-polls) until `status`
   * is `DONE` (with `fileId`/`versionId` populated) or `ERROR`/`EXPIRED`.
   */
  async uploadFile(parentId: string, name: string, content: BlobPart, mimeType = "application/octet-stream"): Promise<TcItem> {
    if (!this.baseUrl) throw new Error("Trimble Connect region has not been resolved yet");
    const token = await this.token();

    const initiate = await fetch(
      `${this.baseUrl}/files/fs/upload?parentId=${encodeURIComponent(parentId)}&parentType=FOLDER`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
    );
    if (!initiate.ok) throw new Error(`Could not start the upload of ${name}: HTTP ${initiate.status}`);
    const init = (await initiate.json()) as FileUploadDetailsResponse;
    const uploadUrl = init.contents?.[0]?.url;
    if (!init.uploadId || !uploadUrl) throw new Error(`Trimble Connect returned no upload URL for ${name}`);

    const put = await fetch(uploadUrl, { method: "PUT", body: content, headers: { "Content-Type": mimeType } });
    if (!put.ok) throw new Error(`Could not upload ${name}: HTTP ${put.status}`);

    for (let attempt = 0; attempt < 30; attempt++) {
      const status = await fetch(
        `${this.baseUrl}/files/fs/upload?uploadId=${encodeURIComponent(init.uploadId)}&wait=true`,
        { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } },
      );
      if (status.ok) {
        const body = (await status.json()) as FileUploadDetailsResponse;
        if (body.status === "DONE" && body.fileId) {
          return { id: body.fileId, name, type: "FILE", versionId: body.versionId };
        }
        if (body.status === "ERROR" || body.status === "EXPIRED") {
          throw new Error(`Upload of ${name} failed: ${body.status}${body.errorReason ? ` (${body.errorReason})` : ""}`);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    throw new Error(`Timed out waiting for Trimble Connect to finish processing ${name}`);
  }

  /**
   * Creates a subfolder inside `parentId`. Used to lazily create the per-dataset
   * "Ausmass - Geo3D Viewer" measurements folder the first time something is saved into it
   * (see `tc/measurementsStore`) — nothing is created just from browsing.
   */
  async createFolder(parentId: string, name: string): Promise<TcItem> {
    if (!this.baseUrl) throw new Error("Trimble Connect region has not been resolved yet");
    const token = await this.token();
    const response = await fetch(`${this.baseUrl}/folders`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ name, parentId }),
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`Could not create folder "${name}": HTTP ${response.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
    }
    const created = (await response.json()) as TcItem;
    return { ...created, type: created.type ?? "FOLDER" };
  }

  /**
   * Permanently deletes a file. Trimble Connect only versions a same-name upload (see
   * `uploadFile`) — there is no "overwrite by content" call — so renaming a measurement's
   * export (its filename is derived from its name) has to delete the old file outright
   * instead of just replacing its content.
   */
  async deleteFile(fileId: string): Promise<void> {
    if (!this.baseUrl) throw new Error("Trimble Connect region has not been resolved yet");
    const token = await this.token();
    const response = await fetch(`${this.baseUrl}/files/${encodeURIComponent(fileId)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Could not delete file: HTTP ${response.status}`);
    }
  }

  private async request<T>(path: string): Promise<T> {
    if (!this.baseUrl) throw new Error("Trimble Connect region has not been resolved yet");
    return retry(async () => {
      const token = await this.token();
      const response = await fetch(`${this.baseUrl}${path}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(`Trimble Connect ${response.status} on ${path}${body ? `: ${body.slice(0, 200)}` : ""}`);
      }
      return (await response.json()) as T;
    }, { attempts: 3 });
  }
}

function orderCandidates(hint?: string): ReadonlyArray<{ name: string; url: string }> {
  const all = [...CORE_API_HOSTS, ...CORE_API_HOSTS_STAGE];
  if (!hint) return all;
  const normalised = hint.toLowerCase();
  const score = (entry: { name: string; url: string }): number => {
    const name = entry.name.toLowerCase();
    if (normalised.includes("europe") || normalised.includes("eu")) return name.startsWith("eu") ? -1 : 0;
    if (normalised.includes("asia") || normalised.includes("ap")) return name.startsWith("ap") ? -1 : 0;
    if (normalised.includes("uk") || normalised.includes("britain")) return name.startsWith("uk") ? -1 : 0;
    if (normalised.includes("america") || normalised.includes("us")) return name.startsWith("us") ? -1 : 0;
    return 0;
  };
  return [...all].sort((a, b) => score(a) - score(b));
}

/** True when a Connect item is a folder. */
export const isFolder = (item: TcItem): boolean => item.type?.toUpperCase() === "FOLDER";
